import 'whatwg-fetch'
import pino from 'pino'

const logger = pino()

// Allow configuring API base URL via environment variable
// For Docker/standalone: set REACT_APP_API_BASE_URL to staging URL
// Default: relative path (works when served from shopify-v1)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || ''
const API_PATH_PREFIX = API_BASE_URL ? `${API_BASE_URL}/apps/ub` : '/apps/ub'

const qs = (params: { [key: string]: any } | undefined | null): string | null => {
  if (typeof params === 'undefined' || params === null) return ''
  const str: string[] = []
  Object.keys(params).forEach(k => {
    const val = params[k]
    if (typeof val === 'undefined' || val === null) {
      str.push(`${k}=`)
      return
    }
    if (Array.isArray(val)) {
      val.forEach(v => {
        str.push(`${k}=${encodeURIComponent(v)}`)
      })
      return
    }
    str.push(`${k}=${encodeURIComponent(val)}`)
  })

  return str.join('&')
}

class SimpleStore {
  private activeRequests: Map<string, AbortController> = new Map()
  private abortableUrls: Array<string | RegExp> = [
    /\/apps\/ub\/ds\/diamond_search.*/
  ]

  private shouldUseAbortController(url: string): boolean {
    return this.abortableUrls.some(pattern => {
      if (typeof pattern === 'string') {
        return url.includes(pattern)
      } else if (pattern instanceof RegExp) {
        return pattern.test(url)
      }
      return false
    })
  }

  async ajax(
    verb: string,
    url: string,
    data?: any,
    signal?: AbortSignal,
    params?: { [key: string]: any },
    options?: { returnErrorResponse?: boolean }
  ): Promise<any> {
    if (params) {
      const query = qs(params)
      if (query) url += `?${query}`
    }
    const requestURL =
      url.indexOf('/') === 0 || url.indexOf('http') === 0 ? url : `${API_PATH_PREFIX}/${url}`
    // Key requests by URL/body and keep background counts separate so they cannot abort searches.
    const requestKey = data ? `${requestURL}::${data}` : requestURL
    const abortKey = signal ? requestKey : `${requestKey}::background`
    let controller: AbortController | undefined
    if (this.shouldUseAbortController(requestURL)) {
      if (this.activeRequests.has(abortKey)) {
        const existingController = this.activeRequests.get(abortKey)
        if (existingController) existingController.abort()
      }
      controller = new AbortController()
      this.activeRequests.set(abortKey, controller)
    }
    let cleanupAbortListeners: (() => void) | undefined
    try {
      const requestInit: RequestInit = {
        method: verb || 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        body: data
      }
      if (controller?.signal && signal) {
        // Both signals present: abort fetch when either fires.
        // Check if either is already aborted before attaching listeners,
        // since addEventListener won't fire for a signal that's already aborted.
        const combinedController = new AbortController()
        if (signal.aborted || controller.signal.aborted) {
          combinedController.abort()
        } else {
          const abort = (): void => combinedController.abort()
          signal.addEventListener('abort', abort, { once: true })
          controller.signal.addEventListener('abort', abort, { once: true })
          cleanupAbortListeners = (): void => {
            signal.removeEventListener('abort', abort)
            controller.signal.removeEventListener('abort', abort)
          }
        }
        requestInit.signal = combinedController.signal
      } else if (controller?.signal) {
        requestInit.signal = controller.signal
      } else if (signal) {
        requestInit.signal = signal
      }
      const resp = await fetch(requestURL, requestInit)
      cleanupAbortListeners?.()
      if (controller) {
        this.activeRequests.delete(abortKey)
      }
      if (resp.status === 200) return resp.json()
      if (resp.status === 401) {
        try {
          localStorage.removeItem('UNBRIDALED_USER')
        } catch (_) {
          // Ignore storage access errors in restricted browser contexts.
        }
        return { status: 401, sessionExpired: true }
      }
      if (options?.returnErrorResponse) {
        const errorData = await resp.json().catch(() => ({}))
        return {
          ...errorData,
          status: resp.status
        }
      }
      throw new Error(`Unexpected Response ${resp.status}`)
    } catch (e) {
      cleanupAbortListeners?.()
      if (e instanceof Error && e.name !== 'AbortError') {
        logger.info(e)
      }
      return null
    }
  }
}

export default SimpleStore
